import React, { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useHistory } from "react-router";
import { Box, TextField, Typography } from "@material-ui/core";
import { ActionLink, CloseButtonIcon, Icon, IconName } from "halifax";
import {
  RecentExperienceSearch,
  ActivitiesEntryTypeEnum,
  CombinedSearchResultType,
  CombinedSearchGroupTrackingType,
} from "redmond";
import {
  getExperiencesAvailabilityQuery,
  getExperiencesShopQuery,
  useIsTravelApp,
} from "@capone/common";
import { connect, ConnectedProps } from "react-redux";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import H from "history";

import { IStoreState } from "../../../reducers/types";
import {
  getMergedCombinedGroups,
  getCombinedAutocompleteLoading,
  getHasCombinedAutocompleteError,
  getTripDestinations,
  getTripDestinationsLoading,
  getEntryTypesAll,
} from "../../search/reducer/selectors";
import {
  fetchCombinedAutocomplete,
  clearCombinedAutocomplete,
  setEntryType,
} from "../../search/actions/actions";
import { CombinedSearchDropdown } from "../../search/components/CombinedSearchDropdown/component";
import { useCombinedSearchAnalytics } from "../../../hooks/useCombinedSearchAnalytics";
import {
  COMBINED_GROUP_TO_ENTRY_TYPE,
  CombinedSearchGroupType,
  CombinedSearchPageContext,
  CombinedSearchSuggestion,
  TripDestination,
} from "../../search/types";
import { PATH_AVAILABILITY, PATH_SHOP } from "../../../utils/paths";
import {
  AVAILABLE,
  COMBINED_SEARCH_IMAGES_EXPERIMENT,
  COMBINED_SEARCH_IMAGES_VARIANTS,
  DATELESS_SEARCH_EXPERIMENT,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  useExperiments,
} from "../../../context/experiments";
import { MobileCalendarPicker } from "../MobileCalendarPicker";
import * as textConstants from "../textConstants";

import "./styles.scss";

enum MobileCombinedSearchStep {
  Search,
  CalendarPicker,
}

interface MobileCombinedSearchOwnProps {
  pageContext: CombinedSearchPageContext;
  destinationId?: string;
  destinationName?: string;
  attractionId?: string;
  categoryId?: string;
  categoryName?: string;
  showTrips?: boolean;
  fromDate?: Date | null;
  untilDate?: Date | null;
  recentSearches?: RecentExperienceSearch[];
  onRecentSearchClick?: (search: RecentExperienceSearch) => void;
  onClose?: () => void;
}

const mapStateToProps = (state: IStoreState) => ({
  combinedGroups: getMergedCombinedGroups(state),
  combinedLoading: getCombinedAutocompleteLoading(state),
  hasCombinedError: getHasCombinedAutocompleteError(state),
  tripDestinations: getTripDestinations(state),
  tripDestinationsLoading: getTripDestinationsLoading(state),
  entryTypesAll: getEntryTypesAll(state),
});

const mapDispatchToProps = {
  fetchCombinedAutocomplete,
  clearCombinedAutocomplete,
  setEntryType,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type MobileCombinedSearchProps = MobileCombinedSearchOwnProps &
  ConnectedProps<typeof connector>;

function getCombinedSearchPlaceholder(
  pageContext: CombinedSearchPageContext,
  destinationName?: string,
  categoryName?: string
): string {
  if (pageContext === "landing") {
    return "Search by name or keyword";
  }
  if (destinationName && categoryName) {
    return `Search ${categoryName} in ${destinationName}`;
  }
  if (destinationName) {
    return `Search in ${destinationName}`;
  }
  return "Search by name or keyword";
}

/* eslint-disable no-shadow -- props shadow action creator imports; standard connect() pattern */
const MobileCombinedSearchComponent = ({
  pageContext,
  destinationId,
  destinationName,
  attractionId,
  categoryId,
  categoryName,
  showTrips = false,
  fromDate = null,
  untilDate = null,
  recentSearches = [],
  onRecentSearchClick,
  onClose,
  combinedGroups,
  combinedLoading,
  hasCombinedError,
  fetchCombinedAutocomplete,
  clearCombinedAutocomplete,
  setEntryType,
  tripDestinations,
  tripDestinationsLoading,
  entryTypesAll,
}: MobileCombinedSearchProps) => {
  /* eslint-enable no-shadow */
  const history = useHistory();
  const { isTravelApp } = useIsTravelApp();
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState(
    pageContext !== "landing" ? destinationName || "" : ""
  );
  const [hasSearched, setHasSearched] = useState(pageContext === "landing");

  const [currentStep, setCurrentStep] = useState(
    MobileCombinedSearchStep.Search
  );
  const [pendingSuggestion, setPendingSuggestion] =
    useState<CombinedSearchSuggestion | null>(null);
  const [pendingGroupType, setPendingGroupType] =
    useState<CombinedSearchGroupType | null>(null);
  const [calendarFrom, setCalendarFrom] = useState<Date | null>(null);
  const [calendarUntil, setCalendarUntil] = useState<Date | null>(null);

  const expState = useExperiments();
  const imageVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    COMBINED_SEARCH_IMAGES_EXPERIMENT,
    COMBINED_SEARCH_IMAGES_VARIANTS
  );

  const isDatelessSearchEnabled =
    getExperimentVariant(expState.experiments, DATELESS_SEARCH_EXPERIMENT) ===
    AVAILABLE;

  const hasMockTripsParam =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("mockTrips");

  const isTripDestinationsEnabled = showTrips || hasMockTripsParam;

  const placeholder = getCombinedSearchPlaceholder(
    pageContext,
    destinationName,
    categoryName
  );

  const hasRecentSearches = recentSearches.length > 0;

  const {
    resetRefs,
    trackFocused,
    trackTyped,
    trackResultClicked,
    trackRecentClicked,
    trackCleared,
    trackNoResults,
  } = useCombinedSearchAnalytics({ pageContext, hasRecentSearches });

  // Reset tracking refs on unmount so re-opening fires events again
  useEffect(
    () => () => {
      resetRefs();
    },
    [resetRefs]
  );

  // Fire no_results when API returns empty
  // Fire typed/no_results events when the API response arrives
  const prevLoadingRef = useRef(combinedLoading);
  useEffect(() => {
    if (prevLoadingRef.current && !combinedLoading && query.trim().length > 0) {
      trackTyped(query);
      if (
        !hasCombinedError &&
        combinedGroups.every((g) => g.suggestions.length === 0)
      ) {
        trackNoResults(query);
      }
    }
    prevLoadingRef.current = combinedLoading;
  }, [
    combinedLoading,
    combinedGroups,
    hasCombinedError,
    query,
    trackTyped,
    trackNoResults,
  ]);

  // Auto-focus and select all text on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 100);
    trackFocused();
    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setQuery(value);
      setHasSearched(true);

      if (value.trim().length > 0) {
        fetchCombinedAutocomplete({
          query: value,
          destinationId,
          attractionId,
          categoryId,
          // Always send the location label on non-landing pages so the saga
          // can both (a) resolve a destinationId when none is set and
          // (b) enforce destination-name filtering on the response when one
          // is set.
          locationLabel:
            pageContext !== "landing" ? destinationName : undefined,
        });
      } else {
        clearCombinedAutocomplete();
      }
    },
    [
      fetchCombinedAutocomplete,
      clearCombinedAutocomplete,
      destinationId,
      attractionId,
      categoryId,
      trackTyped,
      destinationName,
      pageContext,
    ]
  );

  const handleClear = useCallback(() => {
    setQuery("");
    clearCombinedAutocomplete();
    trackCleared();
    inputRef.current?.focus();
  }, [clearCombinedAutocomplete, trackCleared]);

  const navigateWithSuggestion = useCallback(
    (
      suggestion: CombinedSearchSuggestion,
      groupType: CombinedSearchGroupType,
      navFromDate: Date | null,
      navUntilDate: Date | null
    ) => {
      // Store enrichment data for the availability/shop tracking to pick up
      try {
        localStorage.setItem(
          "combined_search_enrichment",
          JSON.stringify({
            label: suggestion.label,
            thumbnailUrl: suggestion.thumbnailUrl,
            activityCount: suggestion.activityCount,
            suggestionType: suggestion.suggestionType || groupType,
          })
        );
      } catch {
        // Ignore localStorage errors
      }

      clearCombinedAutocomplete();
      setQuery("");

      const entryTypeForResult = COMBINED_GROUP_TO_ENTRY_TYPE[groupType];
      if (entryTypeForResult != null) {
        setEntryType(entryTypeForResult);
      }

      switch (groupType) {
        case "Destination":
          history.push(
            `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
              suggestion.label,
              navFromDate,
              navUntilDate,
              undefined,
              isTravelApp,
              ActivitiesEntryTypeEnum.COMBINED_SEARCH_DESTINATION,
              undefined,
              undefined,
              entryTypesAll ?? []
            )}`
          );
          break;

        case "Category":
        case "Attraction": {
          const destName = suggestion.destinationName || destinationName;
          if (!destName) break;
          const entryType =
            groupType === "Category"
              ? ActivitiesEntryTypeEnum.COMBINED_SEARCH_CATEGORY
              : ActivitiesEntryTypeEnum.COMBINED_SEARCH_ATTRACTION;
          const kindSuffix = groupType === "Category" ? "_0" : "_1";
          const filterParam = `&categoryTagId=${suggestion.id}${kindSuffix}`;
          history.push(
            `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
              destName,
              navFromDate,
              navUntilDate,
              undefined,
              isTravelApp,
              entryType,
              undefined,
              undefined,
              entryTypesAll ?? []
            )}${filterParam}`
          );
          break;
        }

        case "Product": {
          const shopFromDate = navFromDate || dayjs().toDate();
          const shopUntilDate = navUntilDate || dayjs().add(7, "day").toDate();
          history.push(
            `${PATH_SHOP}${getExperiencesShopQuery(
              { value: suggestion.id },
              shopFromDate,
              shopUntilDate,
              suggestion.destinationName,
              isTravelApp,
              undefined,
              ActivitiesEntryTypeEnum.COMBINED_SEARCH_PRODUCT,
              entryTypesAll ?? []
            )}`
          );
          break;
        }

        default:
          break;
      }

      if (pageContext !== "landing") {
        onClose?.();
      }
    },
    [
      history,
      destinationName,
      isTravelApp,
      clearCombinedAutocomplete,
      onClose,
      pageContext,
      entryTypesAll,
      setEntryType,
    ]
  );

  const handleResultClick = useCallback(
    (
      suggestion: CombinedSearchSuggestion,
      groupType: CombinedSearchGroupType,
      index: number
    ) => {
      const resultTypeMap: Record<
        CombinedSearchGroupType,
        CombinedSearchResultType
      > = {
        Destination: CombinedSearchResultType.Destination,
        Category: CombinedSearchResultType.Category,
        Attraction: CombinedSearchResultType.Attraction,
        Product: CombinedSearchResultType.Product,
        Collection: CombinedSearchResultType.Collection,
      };

      const groupTypeTrackingMap: Record<
        string,
        CombinedSearchGroupTrackingType
      > = {
        Destination: "Destination",
        Category: "Categories",
        Product: "Products",
      };

      trackResultClicked({
        resultType: resultTypeMap[groupType],
        resultIndex: index,
        groupType: groupTypeTrackingMap[groupType] || groupType,
        searchQuery: query,
        destinationId: suggestion.destinationId,
        destinationName: suggestion.destinationName,
      });

      const hasDates = !!(fromDate && untilDate);
      const needsDatePicker =
        pageContext === "landing" &&
        !hasDates &&
        !isDatelessSearchEnabled &&
        groupType !== "Product";

      if (needsDatePicker) {
        setPendingSuggestion(suggestion);
        setPendingGroupType(groupType);
        setCurrentStep(MobileCombinedSearchStep.CalendarPicker);
        return;
      }

      navigateWithSuggestion(suggestion, groupType, fromDate, untilDate);
    },
    [
      query,
      fromDate,
      untilDate,
      isTravelApp,
      trackResultClicked,
      isDatelessSearchEnabled,
      pageContext,
      navigateWithSuggestion,
    ]
  );

  const handleCalendarSearch = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (_history: H.History) => {
      if (!pendingSuggestion || !pendingGroupType) return;
      navigateWithSuggestion(
        pendingSuggestion,
        pendingGroupType,
        calendarFrom,
        calendarUntil || calendarFrom
      );
    },
    [
      pendingSuggestion,
      pendingGroupType,
      calendarFrom,
      calendarUntil,
      navigateWithSuggestion,
    ]
  );

  const handleCalendarBack = useCallback(() => {
    setCurrentStep(MobileCombinedSearchStep.Search);
    setPendingSuggestion(null);
    setPendingGroupType(null);
    setCalendarFrom(null);
    setCalendarUntil(null);
  }, []);

  const handleRecentSearchClick = useCallback(
    (search: RecentExperienceSearch, index: number) => {
      trackRecentClicked(index, query);
      onRecentSearchClick?.(search);
    },
    [onRecentSearchClick, query, trackRecentClicked]
  );

  const handleTripClick = useCallback(
    (trip: TripDestination, index: number) => {
      trackResultClicked({
        resultType: CombinedSearchResultType.Trip,
        resultIndex: index,
        groupType: "Trip",
        searchQuery: query,
        destinationId: undefined,
        destinationName: trip.displayName,
      });
      setEntryType(ActivitiesEntryTypeEnum.UPCOMING_TRIP);
      history.push(
        `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
          trip.displayName,
          dayjs(trip.tripDate).toDate(),
          dayjs(trip.tripEndDate).toDate(),
          undefined,
          isTravelApp,
          ActivitiesEntryTypeEnum.UPCOMING_TRIP,
          undefined,
          undefined,
          entryTypesAll ?? []
        )}`
      );
    },
    [
      history,
      query,
      isTravelApp,
      trackResultClicked,
      setEntryType,
      entryTypesAll,
    ]
  );

  const isCalendarStep =
    currentStep === MobileCombinedSearchStep.CalendarPicker;

  return (
    <Box className="mobile-combined-search-root">
      {onClose && (
        <Box className="mobile-combined-search-title-bar">
          {isCalendarStep && (
            <ActionLink
              className="mobile-combined-search-back"
              onClick={handleCalendarBack}
              content={<FontAwesomeIcon icon={faChevronLeft} />}
              label="Back"
            />
          )}
          <Typography className="mobile-combined-search-title">
            {isCalendarStep ? textConstants.UPDATE_CALENDAR_TEXT : "Activities"}
          </Typography>
          <ActionLink
            className="mobile-combined-search-close"
            onClick={onClose}
            content={<CloseButtonIcon className="close-icon" />}
            label="Close"
            showTappableArea
          />
        </Box>
      )}
      {isCalendarStep ? (
        <MobileCalendarPicker
          fromDate={calendarFrom}
          untilDate={calendarUntil}
          setFromDate={setCalendarFrom}
          setUntilDate={setCalendarUntil}
          handleSearch={handleCalendarSearch}
        />
      ) : (
        <>
          <Box className="mobile-combined-search-header">
            <Box className="mobile-combined-search-input-wrapper">
              <Icon name={IconName.B2BMapPin} ariaLabel="" aria-hidden />
              <TextField
                inputRef={inputRef}
                className="mobile-combined-search-input"
                placeholder={placeholder}
                value={query}
                onChange={handleInputChange}
                fullWidth
                InputProps={{ disableUnderline: true }}
              />
              {query.length > 0 && (
                <button
                  className="mobile-combined-search-clear"
                  onClick={handleClear}
                  aria-label="Clear search"
                  type="button"
                >
                  <CloseButtonIcon className="clear-icon" />
                </button>
              )}
            </Box>
          </Box>

          <Box className="mobile-combined-search-results">
            <CombinedSearchDropdown
              groups={combinedGroups}
              loading={combinedLoading}
              hasError={hasCombinedError}
              query={query}
              recentSearches={recentSearches}
              tripDestinations={tripDestinations}
              showTrips={isTripDestinationsEnabled}
              tripsLoading={tripDestinationsLoading}
              imageVariant={imageVariant}
              hasSearched={hasSearched}
              onResultClick={handleResultClick}
              onRecentSearchClick={handleRecentSearchClick}
              onTripClick={handleTripClick}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export const MobileCombinedSearch = connector(MobileCombinedSearchComponent);
